import { Editor } from "@monaco-editor/react";
import clsx from "clsx";
import { ReactElement, useCallback, useEffect, useState } from "react";

import { processLiquidTemplate } from "../../services/integrations/process-liquid-template";
import { Button } from "../catalyst/Button";

export type LiquidSnippetPopupProps = {
  left: number;
  onClose: () => void;
  onInsert: (snippet: string) => void;
  top: number;
};

const LiquidSnippetPopup = ({
  left,
  onClose,
  onInsert,
  top,
}: LiquidSnippetPopupProps): ReactElement => {
  const [liquidTemplate, setLiquidTemplate] = useState<string>("");
  const [renderedValue, setRenderedValue] = useState<string>("");
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const evaluateTemplate = useCallback((template: string) => {
    if (!template.trim()) {
      setRenderedValue("");
      setError(null);
      return;
    }

    setIsEvaluating(true);
    setError(null);

    const processFn = processLiquidTemplate(
      template,
      (result) => {
        setRenderedValue(result);
        setError(null);
        setIsEvaluating(false);
      },
      (errorMessage) => {
        setError(errorMessage);
        setRenderedValue("");
        setIsEvaluating(false);
      },
    );
    processFn();
  }, []);

  useEffect(() => {
    evaluateTemplate(liquidTemplate);
  }, [liquidTemplate, evaluateTemplate]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        onInsert(liquidTemplate);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return (): void => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, onInsert, liquidTemplate]);

  return (
    <div
      className="fixed z-[10000] w-[500px] rounded-[10px] border border-white/10 bg-transparent p-3 text-xs text-white shadow-[0_20px_40px_rgba(0,0,0,0.35)] dark:bg-zinc-900/75"
      onMouseDown={(e) => {
        // Only prevent default if clicking on the container itself, not its children
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      }}
      style={{ left, top }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-bold">Insert liquid snippet</div>
        <Button
          aria-label="Close"
          className="h-4 w-4 border-0 bg-transparent p-0 text-gray-400 hover:text-gray-300"
          onClick={onClose}
          plain
          size="xs"
        >
          ×
        </Button>
      </div>

      {/* Liquid Template Editor */}
      <div className="mt-3">
        <div className="mb-1.5 text-xs text-gray-400">Liquid Template</div>
        <div className="overflow-hidden rounded-lg border border-gray-800">
          <Editor
            height="36px"
            language="liquid"
            onChange={(value) => setLiquidTemplate(value || "")}
            options={{
              automaticLayout: true,
              fontSize: 12,
              lineNumbers: "off",
              links: false,
              minimap: { enabled: false },
              padding: { bottom: 4, top: 4 },
              quickSuggestions: false,
              readOnly: false,
              scrollbar: { horizontal: "hidden", vertical: "hidden" },
              scrollBeyondLastLine: false,
              "semanticHighlighting.enabled": true,
              suggestOnTriggerCharacters: true,
              wordBasedSuggestions: "off",
              wordWrap: "off",
            }}
            theme="miru-dark"
            value={liquidTemplate}
            width="100%"
          />
        </div>
      </div>
      <div className="mt-3">
        <div className="mb-1.5 text-xs text-gray-400">Rendered Value</div>
        <div
          className={clsx(
            "overflow-hidden rounded-lg border border-gray-800 px-3 py-2 text-xs",
            error ? "text-red-400" : "text-white",
          )}
        >
          {isEvaluating
            ? "Evaluating..."
            : error
              ? `Error: ${error}`
              : renderedValue}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2">
        <Button onClick={onClose} outline size="xs">
          Cancel
        </Button>
        <Button
          disabled={!liquidTemplate.trim() || !!error}
          onClick={() => onInsert(liquidTemplate)}
          size="xs"
        >
          Insert
        </Button>
      </div>
    </div>
  );
};

export default LiquidSnippetPopup;
